"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import makeStyles from "../../theming/make-styles.js";
import Portal from "../portal/index.js";
import clamp from "../utils/clamp.js";
import useCombinedRefs from "../utils/use-combined-refs.js";
import useScrollLock from "../utils/use-scroll-lock.js";
const MINIMAL_ATTACHMENT_MARGIN = 16;
const SELECT_MARGIN = 4;
const useStyles = makeStyles()((_, position) => {
    return {
        attachment: Object.assign({ boxSizing: "content-box", position: "absolute", zIndex: 1020, overflow: "hidden", overflowY: "auto" }, position),
    };
});
export const Attachment = React.forwardRef(function Attachment(_a, ref) {
    var { anchorRef, children, className, scrollLock = true, strategy = "anchor-size", onClose = () => { } } = _a, props = __rest(_a, ["anchorRef", "children", "className", "scrollLock", "strategy", "onClose"]);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, maxHeight: 0 });
    const { classes, cx } = useStyles(coords);
    const innerRef = useRef(ref);
    const combinedRef = useCombinedRefs(ref, innerRef);
    useScrollLock(scrollLock, combinedRef);
    const updateCoords = useCallback(function updateCoords() {
        var _a, _b, _c;
        const anchorRect = (_a = anchorRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (!combinedRef || !("current" in combinedRef)) {
            return;
        }
        const attachmentRect = (_c = (_b = combinedRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect) === null || _c === void 0 ? void 0 : _c.call(_b);
        if (!anchorRect || !attachmentRect)
            return;
        const placeOnTop = anchorRect.top;
        const placeOnBottom = window.innerHeight - anchorRect.bottom;
        const maxHeight = Math.max(window.innerHeight - anchorRect.bottom, anchorRect.top) - MINIMAL_ATTACHMENT_MARGIN;
        const { left: anchorLeft, width, bottom } = anchorRect;
        const top = window.scrollY +
            (attachmentRect.height < placeOnBottom || placeOnBottom > placeOnTop ? bottom + SELECT_MARGIN : anchorRect.top - SELECT_MARGIN - attachmentRect.height);
        const attachmentWidth = (attachmentRect === null || attachmentRect === void 0 ? void 0 : attachmentRect.width) || 0;
        let left;
        switch (strategy) {
            case "anchor-size":
            case "align-left": {
                left = anchorLeft;
                break;
            }
            case "align-center": {
                left = anchorLeft + (width - attachmentWidth) / 2;
                break;
            }
            case "align-right": {
                left = anchorLeft + width - attachmentWidth;
                break;
            }
        }
        left = clamp(0, left, document.body.clientWidth - attachmentWidth - MINIMAL_ATTACHMENT_MARGIN);
        const newCoords = {
            top: Math.floor(top),
            left: Math.floor(left),
            width: strategy === "anchor-size" ? Math.floor(width) : undefined,
            maxHeight: Math.floor(maxHeight),
        };
        if (!coords ||
            coords.top !== newCoords.top ||
            coords.left !== newCoords.left ||
            (strategy === "anchor-size" && coords.width !== newCoords.width) ||
            coords.maxHeight !== newCoords.maxHeight) {
            setCoords(newCoords);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [anchorRef, children, coords, ref, strategy]);
    useLayoutEffect(() => {
        updateCoords();
    }, [updateCoords]);
    useEffect(() => {
        window.addEventListener("resize", onClose);
        return () => {
            window.removeEventListener("resize", onClose);
        };
    }, [onClose]);
    if (!anchorRef) {
        return null;
    }
    return (_jsx(Portal, { children: _jsx("div", Object.assign({ ref: combinedRef, className: cx(classes.attachment, className), "data-component": "attachment" }, props, { children: children })) }));
});
export default Attachment;
//# sourceMappingURL=attachment.js.map